/**
 * Scene mood mapping.
 *
 * Maps scene background slugs (e.g. "lethe-archive") to ambient audio mood ids
 * (e.g. "archive-tension"). Each mood id corresponds to a single ambient track
 * shipped under `assets/audio/bgm/<mood>.mp3`.
 *
 * Keep this list small and reusable. We intentionally share moods across many
 * scenes so the soundtrack is cohesive and ambience never becomes distracting.
 */

export type SceneMood =
  | 'domestic-warm'
  | 'neutral-clinic'
  | 'archive-tension'
  | 'memory-distort'
  | 'confrontation'
  | 'ending-expose'
  | 'ending-erase'
  | 'ending-restore';

export const AVAILABLE_MOODS: readonly SceneMood[] = [
  'domestic-warm',
  'neutral-clinic',
  'archive-tension',
  'memory-distort',
  'confrontation',
  'ending-expose',
  'ending-erase',
  'ending-restore',
] as const;

const MOOD_BY_SUBSTRING: Array<{ test: (slug: string) => boolean; mood: SceneMood }> = [
  // Domestic / warm
  { test: (s) => s.startsWith('apartment-morning'), mood: 'domestic-warm' },
  { test: (s) => s.startsWith('apartment-evening'), mood: 'domestic-warm' },
  { test: (s) => s.startsWith('lina-bedroom'), mood: 'domestic-warm' },
  { test: (s) => s.startsWith('linas-room-empty'), mood: 'memory-distort' },

  // Memory / distortion
  { test: (s) => s.startsWith('memory-fragment'), mood: 'memory-distort' },
  { test: (s) => s.startsWith('mira-bedroom'), mood: 'memory-distort' },
  { test: (s) => s.startsWith('apartment-night'), mood: 'memory-distort' },

  // Archive / institutional
  { test: (s) => s.startsWith('lethe-archive'), mood: 'archive-tension' },
  { test: (s) => s.startsWith('lethe-edit-room'), mood: 'archive-tension' },
  { test: (s) => s.startsWith('lethe-corridor'), mood: 'archive-tension' },
  { test: (s) => s.startsWith('lethe-exterior-night'), mood: 'archive-tension' },
  { test: (s) => s.startsWith('edit-suite-dark'), mood: 'archive-tension' },

  // Neutral clinic / office
  { test: (s) => s.startsWith('lethe-lobby'), mood: 'neutral-clinic' },
  { test: (s) => s.startsWith('lethe-exterior'), mood: 'neutral-clinic' },
  { test: (s) => s.startsWith('mira-office'), mood: 'neutral-clinic' },
  { test: (s) => s.startsWith('iris-office'), mood: 'neutral-clinic' },
  { test: (s) => s.startsWith('rooks-office'), mood: 'neutral-clinic' },
  { test: (s) => s.startsWith('small-clinic'), mood: 'neutral-clinic' },
  { test: (s) => s.startsWith('train-platform'), mood: 'neutral-clinic' },
  { test: (s) => s.startsWith('conference-room'), mood: 'neutral-clinic' },

  // Confrontation
  { test: (s) => s.startsWith('hearing-room'), mood: 'confrontation' },

  // Endings (only used when explicit bgm is provided; not slug-based normally)
];

/**
 * Resolve a stable mood id for a given scene background slug.
 * Unknown backgrounds map to "neutral-clinic" (safe, low-presence ambience).
 */
export function resolveSceneMood(bgSlug: string): SceneMood {
  for (const rule of MOOD_BY_SUBSTRING) {
    if (rule.test(bgSlug)) return rule.mood;
  }
  return 'neutral-clinic';
}

/** Asset path for a given mood id or arbitrary BGM name. */
export function moodToBgmPath(mood: SceneMood | string): string {
  return `./assets/audio/bgm/${mood}.mp3`;
}
